export interface QueueTask {
  entity: string
  resourceId: string | number
  action: 'create'
  data: Record<string, any>
  createdOn?: Date | string
}

export const OFFLINE_QUEUE_SNAPSHOT_KEY = 'fuellox.offlineQueue.snapshot'
export const OFFLINE_QUEUE_FETCH_INTERVAL_MINUTES = 15

export interface OfflineQueueSnapshot {
  apiEndpoint: string
  token: string
  tenantId: string
  orgId: string
  tasks: QueueTask[]
  uploadedResourceIds: string[]
}

export function queueTaskUrl (apiEndpoint: string, entity: string): string {
  return `${apiEndpoint.replace(/\/$/, '')}/${entity}`
}

export function queueTaskBody (task: QueueTask): Record<string, unknown> {
  return {
    ...task.data,
    id: task.resourceId,
  }
}

export function resourceIdKey (resourceId: string | number): string {
  return String(resourceId)
}

export function applyUploadedIds (
  tasks: QueueTask[],
  uploadedResourceIds: string[],
): QueueTask[] {
  if (uploadedResourceIds.length === 0) {
    return tasks
  }
  const uploaded = new Set(uploadedResourceIds)
  return tasks.filter(task => !uploaded.has(resourceIdKey(task.resourceId)))
}

export function parseOfflineQueueSnapshot (raw: string | null | undefined): OfflineQueueSnapshot | null {
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as Partial<OfflineQueueSnapshot>
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    return {
      apiEndpoint: typeof parsed.apiEndpoint === 'string' ? parsed.apiEndpoint : '',
      token: typeof parsed.token === 'string' ? parsed.token : '',
      tenantId: typeof parsed.tenantId === 'string' ? parsed.tenantId : '',
      orgId: typeof parsed.orgId === 'string' ? parsed.orgId : '',
      tasks: Array.isArray(parsed.tasks) ? parsed.tasks : [],
      uploadedResourceIds: Array.isArray(parsed.uploadedResourceIds)
        ? parsed.uploadedResourceIds.map(id => String(id))
        : [],
    }
  } catch {
    return null
  }
}

export function serializeOfflineQueueSnapshot (snapshot: OfflineQueueSnapshot): string {
  return JSON.stringify(snapshot)
}

export function mergeUploadedIds (
  existing: string[],
  extra: string[],
): string[] {
  return [...new Set([...existing, ...extra])]
}

export function hydrateQueueFromSnapshot (
  storedTasks: QueueTask[],
  snapshot: OfflineQueueSnapshot | null | undefined,
): QueueTask[] {
  const remaining = applyUploadedIds(
    snapshot?.tasks ?? [],
    snapshot?.uploadedResourceIds ?? [],
  )
  if (storedTasks.length === 0) {
    return remaining
  }
  if (remaining.length === 0) {
    return storedTasks
  }
  const storedIds = new Set(storedTasks.map(task => resourceIdKey(task.resourceId)))
  const extras = remaining.filter(task => !storedIds.has(resourceIdKey(task.resourceId)))
  return extras.length === 0 ? storedTasks : [...storedTasks, ...extras]
}

export function shouldWriteOfflineQueueSnapshot (input: {
  nextTaskCount: number
  existingRemainingCount: number
  sessionHadTasks: boolean
}): boolean {
  if (
    input.nextTaskCount === 0 &&
    input.existingRemainingCount > 0 &&
    !input.sessionHadTasks
  ) {
    return false
  }
  return true
}

export function logoutClearsOfflineQueueSnapshot (): boolean {
  return false
}

export function buildOfflineQueueSnapshot (input: {
  apiEndpoint: string
  token: string
  tenantId: string
  orgId: string
  tasks: QueueTask[]
  uploadedResourceIds?: string[]
}): OfflineQueueSnapshot {
  const uploadedResourceIds = input.uploadedResourceIds ?? []
  return {
    apiEndpoint: input.apiEndpoint,
    token: input.token,
    tenantId: input.tenantId,
    orgId: input.orgId,
    tasks: applyUploadedIds(input.tasks, uploadedResourceIds),
    uploadedResourceIds,
  }
}

export function isSuccessfulQueueStatus (status: number): boolean {
  return status >= 200 && status < 300
}

export async function drainQueuedCreates (
  tasks: QueueTask[],
  post: (task: QueueTask) => Promise<boolean>,
): Promise<{ remaining: QueueTask[], uploadedResourceIds: string[] }> {
  const remaining: QueueTask[] = []
  const uploadedResourceIds: string[] = []

  for (const task of tasks) {
    if (task.action !== 'create' || !task.entity) {
      remaining.push(task)
      continue
    }
    const uploaded = await post(task)
    if (uploaded) {
      uploadedResourceIds.push(resourceIdKey(task.resourceId))
    } else {
      remaining.push(task)
    }
  }

  return { remaining, uploadedResourceIds }
}

export function shouldDrainOnLeavingForeground (
  isActive: boolean,
  platform: string,
): boolean {
  return !isActive && platform !== 'web'
}

export function buildBackgroundFetchConfig (networkTypeAny: 0 | 1 | 2 | 3 | 4): {
  minimumFetchInterval: number
  stopOnTerminate: boolean
  startOnBoot: boolean
  enableHeadless: boolean
  requiredNetworkType: 0 | 1 | 2 | 3 | 4
} {
  return {
    minimumFetchInterval: OFFLINE_QUEUE_FETCH_INTERVAL_MINUTES,
    stopOnTerminate: false,
    startOnBoot: true,
    enableHeadless: true,
    requiredNetworkType: networkTypeAny,
  }
}
